import PersonPinRounded from '@mui/icons-material/PersonPinRounded';
import { LoadingButton } from '@mui/lab';
import { Box, Skeleton, Typography, useMediaQuery } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { FC, useEffect, useState } from 'react';

import { Piece, RecipientAutoDetectionStatus } from '@apiContract';
import { useAssignmentRecipient } from '@components/Capture/hooks/useAssignmentRecipient';
import { useUpdateQueue } from '@components/Capture/hooks/useUpdateQueue';
import { Iconify } from '@components/Iconify';
import { useUserInfoLines } from 'src/hooks/useUserInfoLines';

import {
  mdNotification,
  scrollContainer,
  sxIcon,
  sxNotification,
  sxNotificationAction,
  sxNotificationText,
} from './styles';

type Props = {
  piece?: Piece | null;
};

const RecipientNotification: FC<Props> = ({ piece }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isVisible, setIsVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { addEvent } = useUpdateQueue({ context: 'MailPiece' });
  const { assignRecipientToPiece } = useAssignmentRecipient(addEvent);
  const pieceRecipient = piece?.recipient || piece?.recipientAutoDetection?.recipient;
  const { idLine, routesLine, isRoutesLoading } = useUserInfoLines(pieceRecipient);
  const pieceStatus = piece?.recipientAutoDetection?.status;

  const isConfirm =
    !piece?.recipient && pieceRecipient && pieceStatus === RecipientAutoDetectionStatus.IdentifiedVerificationRequired;

  const onConfirm = () => {
    setIsLoading(true);
    if (isConfirm && piece) {
      assignRecipientToPiece(pieceRecipient, piece);
    }
  };

  useEffect(() => {
    if (pieceRecipient) {
      setIsVisible(true);
      setIsLoading(false);
    }
  }, [pieceRecipient]);

  const onClose = () => {
    setIsVisible(false);
  };

  const color = isConfirm ? 'warning' : 'success';

  return (
    isVisible && (
      <Box sx={isMobile ? sxNotification : mdNotification}>
        <Box sx={scrollContainer} className="customScrollBar">
          <Box sx={sxIcon(color)}>
            <PersonPinRounded color={color} />
          </Box>
          <Box sx={sxNotificationText}>
            <Typography variant="subtitle2">{pieceRecipient?.displayName} </Typography>
            <Box>
              <Typography variant="body2" sx={{ color: 'grey.500' }}>
                {idLine}
              </Typography>
            </Box>
            {routesLine &&
              (isRoutesLoading ? (
                <Skeleton variant="text" width="40%" />
              ) : (
                <Box sx={{ display: 'flex', textWrap: 'nowrap' }}>
                  <Iconify
                    icon={'material-symbols:mode-of-travel'}
                    width={16}
                    height={16}
                    color={theme.palette.text.secondary}
                    sx={{ m: '0 4px 0 0' }}
                  />
                  <Typography variant="caption" sx={{ color: 'grey.500' }}>
                    {routesLine}
                  </Typography>
                </Box>
              ))}
          </Box>
          <Box sx={sxNotificationAction}>
            {isConfirm ? (
              <LoadingButton
                variant="soft"
                size="small"
                color="warning"
                onClick={onConfirm}
                loading={isLoading}
                sx={{ mr: 1, ml: 1 }}
              >
                Confirm
              </LoadingButton>
            ) : (
              <IconButton size="small" onClick={onClose} sx={{ p: 0.5, mr: 0.5 }}>
                <Iconify width={16} icon="mingcute:close-line" />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    )
  );
};

export default RecipientNotification;
